import { Form, Formik } from "formik";
import * as yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@volca/ui/components/ui/card";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { useSubmitTechFeedback } from "./query";

interface TechFeedbackFormValues {
  name: string;
  email: string;
  inquiry: string;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  inquiry: yup
    .string()
    .required("Inquiry is required")
    .min(10, "Inquiry must be at least 10 characters")
    .max(1500, "Inquiry must be less than 1500 characters"),
});

const initialValues: TechFeedbackFormValues = {
  name: "",
  email: "",
  inquiry: "",
};

const TechFeedback = () => {
  const { mutate: submitFeedback, isPending } = useSubmitTechFeedback();

  const handleSubmit = (
    values: TechFeedbackFormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    submitFeedback(values, {
      onSuccess: () => {
        toast.success("Thank you for your feedback!", {
          description: "We'll get back to you as soon as possible.",
        });
        resetForm();
      },
      onError: (error) => {
        toast.error("Couldn't send your feedback", {
          description: error.errors?.join(", ") || "Please try again later.",
        });
      },
    });
  };

  return (
    <Stack
      align="start"
      gap={4}
      className="min-h-full w-full items-center justify-center p-4"
    >
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <Text variant="title" render={<h1 />}>
            Tech feedback
          </Text>
          <CardDescription>
            Have a question or suggestion? We'd love to hear from you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ getFieldProps, errors, touched }) => (
              <Form>
                <Stack align="start" gap={6}>
                  <FormInput
                    label="Name"
                    placeholder="Your name"
                    required
                    fullWidth
                    {...getFieldProps("name")}
                  />

                  <FormInput
                    label="Work email"
                    type="email"
                    placeholder="work.email@example.com"
                    required
                    fullWidth
                    {...getFieldProps("email")}
                  />

                  <Stack className="w-full" align="start" gap={1}>
                    <Stack gap={2} className="w-full">
                      <Label
                        htmlFor="tech-feedback-inquiry"
                        className="gap-0.5"
                      >
                        Inquiry
                        <span aria-hidden>*</span>
                      </Label>
                      <Textarea
                        id="tech-feedback-inquiry"
                        className="min-h-32"
                        placeholder="Tell us what's on your mind"
                        {...getFieldProps("inquiry")}
                      />
                    </Stack>
                    {touched.inquiry && errors.inquiry && (
                      <Text className="text-danger">{errors.inquiry}</Text>
                    )}
                  </Stack>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isPending}
                    loading={isPending}
                  >
                    Submit feedback
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default TechFeedback;
